/**
 * Use featured promoted cards for content that warrants the second highest level of prominence
 *
 * @module views/components/FeaturedPromotedCard
 * @memberof -Common
 */
import './FeaturedPromotedCard.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import { isAuthButton } from '@ulta/utils/actionUtils/actionUtils';
import { constants } from '@ulta/utils/constants/constants';

/**
  * Represents a FeaturedPromotedCard component
  *
  * @method
  * @param {FeaturedPromotedCardProps} props - React properties passed from composition
  * @returns FeaturedPromotedCard
  */
export const FeaturedPromotedCard = function( props ){

  const featuredPromotedCardRef = useRef( null );
  const [currentFeaturedPromotedCard, setCurrentFeaturedPromotedCard] = useState( '' );
  const { rootMargin = defaultProps.rootMargin, root = defaultProps.root, threshold = defaultProps.threshold } = props;

  const isIntersecting = useIntersectionObserver( featuredPromotedCardRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  useEffect( () => {
    // set featuredPromotedCardRef
    if( featuredPromotedCardRef.current ){
      setCurrentFeaturedPromotedCard( featuredPromotedCardRef.current );
    }
  }, [] );

  return (
    <div className='FeaturedPromotedCard'
      ref={ featuredPromotedCardRef }
    >
      <div className={
        classNames( 'FeaturedPromotedCard__wrapper', {
          [`FeaturedPromotedCard__wrapper--contentRows`]: props.backgroundImage
        } )
      }
      role='group'
      aria-label={ 'product' }
      >
        <div className={ classNames( 'FeaturedPromotedCard__backgroundColor FeaturedPromotedCard__foregroundLayer',
          ( props.backgroundColor && `FeaturedPromotedCard__backgroundColor--${props.backgroundColor}` ) ) }
        ></div>
        <div className='FeaturedPromotedCard__textContent FeaturedPromotedCard__foregroundLayer'>
          {
            props.tag &&
            <div className='FeaturedPromotedCard__tag'>
              <Tag
                variant='default'
                style='white'
                message={ props.tag }
              />
            </div>
          }
          <div className='FeaturedPromotedCard__eyebrow'>
            <Text
              htmlTag='span'
              textStyle='eyebrow'
            >
              { props.eyebrow }
            </Text>
          </div>
          <div className='FeaturedPromotedCard__headline'>
            <Text
              htmlTag='h2'
              textStyle='title-3'
            >
              { props.headline }
            </Text>
          </div>
          <div className='FeaturedPromotedCard__subheadline'>
            <Text
              htmlTag='p'
              textStyle='body-2'
            >
              { props.subheadline }
            </Text>
          </div>
          <div className='FeaturedPromotedCard__actions'>
            {
              ( ( props.primaryAction && props.primaryAction.label ) || ( props.secondaryAction && props.secondaryAction.label ) ) &&
              <div className={ classNames( 'FeaturedPromotedCard__actionsButton',
                { 'FeaturedPromotedCard__actionsButton--fullwidth': ( ( props.primaryAction && props.primaryAction.label ) && ( props.secondaryAction && props.secondaryAction.label ) ) } ) }
              >
                {
                  props.primaryAction && props.primaryAction.label &&
                  <div className='FeaturedPromotedCard__button' >
                    {
                      !isAuthButton( props.primaryAction ) &&
                        <Button
                          label={ props.primaryAction.label }
                          target='_self'
                          url={ props.primaryAction.url }
                          action={ props.primaryAction }
                          compact
                          ariaLabel={ props.primaryAction.label + ' ' + props.headline }
                          withHover
                          className={
                            classNames( `FeaturedPromotedCard__content--${props.backgroundColor}`, props.backgroundColor, {
                              'FeaturedPromotedCard__primaryButton': props.primaryAction.label
                            } )
                          }
                        />
                    }
                    {
                      isAuthButton( props.primaryAction ) &&
                        <Link_Huge
                          target='_self'
                          action={ props.primaryAction }
                          compact
                          likeButtonPrimary
                          ariaLabel={ props.primaryAction.label + ' ' + props.headline }
                          className={
                            classNames( `FeaturedPromotedCard__content--${props.backgroundColor}`, props.backgroundColor, {
                              'FeaturedPromotedCard__primaryButton': props.primaryAction.label
                            } )
                          }
                        >
                          { props.primaryAction.label }
                        </Link_Huge>
                    }
                  </div>
                }
                {
                  props.secondaryAction && props.secondaryAction.label &&
                  <div className='FeaturedPromotedCard__button' >
                    {
                      !isAuthButton( props.secondaryAction ) &&
                        <Button
                          label={ props.secondaryAction.label }
                          target='_self'
                          url={ props.secondaryAction.url }
                          ariaLabel={ props.secondaryAction.label + ' ' + props.headline }
                          action={ props.secondaryAction }
                          compact
                          secondary
                          withHover
                          className={ props.backgroundColor && `FeaturedPromotedCard__content--${props.backgroundColor}` }
                        />
                    }
                    {
                      isAuthButton( props.secondaryAction ) &&
                        <Link_Huge
                          target='_self'
                          action={ props.primaryAction }
                          compact
                          ariaLabel={ props.primaryAction.label + ' ' + props.headline }
                          className={
                            classNames( `FeaturedPromotedCard__content--${props.backgroundColor}`, props.backgroundColor, {
                              'FeaturedPromotedCard__primaryButton': props.primaryAction.label
                            } )
                          }
                        >
                          { props.primaryAction.label }
                        </Link_Huge>

                    }
                  </div>
                }
              </div>
            }
            {
              ( ( props.linkAction1 && props.linkAction1.label ) || ( props.linkAction2 && props.linkAction2.label ) ) &&
              <div className='FeaturedPromotedCard__actionsLink'>
                {
                  props.linkAction1 && props.linkAction1.label &&
                  <div className='FeaturedPromotedCard__actionsLink--label'>
                    <Link_Huge
                      target='_self'
                      action={ props.linkAction1 }
                      withHover
                      ariaLabel={ props.linkAction1.label + ' ' + props.headline }
                    >
                      { props.linkAction1.label }
                    </Link_Huge>
                  </div>
                }
                {
                  props.linkAction2 && props.linkAction2.label &&
                  <div className='FeaturedPromotedCard__actionsLink--label'>
                    <Link_Huge
                      target='_self'
                      action={ props.linkAction2 }
                      withHover
                      ariaLabel={ props.linkAction2.label + ' ' + props.headline }
                    >
                      { props.linkAction2.label }
                    </Link_Huge>
                  </div>
                }
              </div>
            }
          </div>
        </div>
        <div className='FeaturedPromotedCard__image FeaturedPromotedCard__foregroundLayer'>
          {
            props.image &&
            <UltaAsset
              { ...spreadProps( { ...props.image } ) }
            />
          }
        </div>
        {
          props.backgroundImage &&
          <div className='FeaturedPromotedCard__pattern'>
            <UltaAsset
              { ...spreadProps( { ...props.backgroundImage } ) }
              action={ { url :false } }
            />
          </div>
        }
      </div>
    </div>
  );
};

/**
  * Property type definitions
  * @typedef FeaturedPromotedCardProps
  * @type {object}
  * @property {object} image - Sets the  image
  * @property {string} tag - Sets the tag
  * @property {string} eyebrow - Sets the eyebrow
  * @property {string} headline - Sets the headline
  * @property {string} subheadline - Sets the subheadline
  * @property {string} backgroundColor - Sets the background color
  * @property {object} backgroundImage - Sets the background image
  * @property {string} altText - Sets the alt text
  * @property {object} primaryAction - Sets the primary action
  * @property {object} secondaryAction - Sets the secondary action
  * @property {object} linkAction1 - Sets the first link action
  * @property {object} linkAction2 - Sets the second link action
  */

export const propTypes = {
  image: PropTypes.object,
  tag: PropTypes.string,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  subheadline: PropTypes.string,
  backgroundColor: PropTypes.string,
  backgroundImage: PropTypes.object,
  altText: PropTypes.string,
  primaryAction: PropTypes.object,
  secondaryAction: PropTypes.object,
  linkAction1: PropTypes.object,
  linkAction2: PropTypes.object
};
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};
FeaturedPromotedCard.defaultProps = defaultProps;
FeaturedPromotedCard.propTypes = propTypes;

export default FeaturedPromotedCard;